import type { AIScan } from "@/lib/scanStore";

/**
 * Convert the raw fig-verify edge function payload into the AIScan shape the
 * Result screen and the `scans` table consume. Lives here (rather than inside
 * Analyze.tsx) so it can be reused when we hydrate Result from a row that
 * was completed in the background.
 *
 * `userBrandFallback` is the brand the submitter typed into the optional
 * brand field at scan time. Used when the model returns null brand_detected
 * (e.g. no-label items) so the title still includes the brand the user
 * identified.
 */
export function figVerifyToAIScan(
  v: Record<string, unknown>,
  userBrandFallback?: string,
): AIScan {
  const item = (v?.item ?? {}) as Record<string, unknown>;
  const scores = (v?.scores ?? {}) as Record<string, unknown>;
  const observations = (v?.observations ?? {}) as Record<string, unknown>;
  const assessment = (v?.assessment ?? {}) as Record<string, unknown>;
  const pricing = (v?.pricing ?? null) as Record<string, unknown> | null;
  const meta = (v?.meta ?? {}) as Record<string, unknown>;

  const verdictKey = String(assessment?.verdict ?? "");
  const isPass = verdictKey === "authentic_vintage" || verdictKey === "likely_vintage";
  const verdictLabelMap: Record<string, string> = {
    authentic_vintage: "FIG Verified",
    likely_vintage: "Likely Vintage",
    inconclusive: "Inconclusive",
    unlikely_vintage: "Unlikely Vintage",
    not_vintage: "Not Vintage",
    insufficient_evidence: "More Photos Needed",
  };
  const confMap: Record<string, number> = { high: 92, medium: 75, low: 55, insufficient: 30 };
  const confidence = confMap[String(assessment?.overall_confidence ?? "medium")] ?? 70;

  const guidance = (pricing?.guidance ?? null) as Record<string, unknown> | null;
  const parseRange = (s: string | undefined): [number, number] => {
    if (!s) return [0, 0];
    const nums = String(s).match(/\d+(?:\.\d+)?/g)?.map(Number) ?? [];
    if (nums.length >= 2) return [nums[0], nums[1]];
    if (nums.length === 1) return [nums[0], nums[0]];
    return [0, 0];
  };
  const [recLow, recHigh] = parseRange(guidance?.recommended_range as string | undefined);
  const suggested = recLow && recHigh ? Math.round((recLow + recHigh) / 2) : recLow || recHigh || 0;
  const comparableCount = Number(pricing?.comparable_count ?? 0) || 0;
  const dataSource = (pricing?.data_source ?? "none") as "serpapi" | "dataforseo" | "market" | "none";

  const forensic: { label: string; value: string; confidence: number }[] = [];
  if (scores.label_note)
    forensic.push({ label: "Main label", value: String(scores.label_note), confidence: Number(scores.label_authenticity ?? 0) });
  if (scores.construction_note)
    forensic.push({ label: "Construction", value: String(scores.construction_note), confidence: Number(scores.construction_era ?? 0) });
  if (scores.material_note)
    forensic.push({ label: "Material", value: String(scores.material_note), confidence: Number(scores.material_consistency ?? 0) });

  const tags: string[] = Array.isArray(observations?.special_characteristics)
    ? (observations.special_characteristics as string[])
    : [];
  const notes: string[] = Array.isArray(assessment?.era_indicators)
    ? (assessment.era_indicators as string[])
    : [];

  // Extract country of origin from label_details text (same regex used in Result.tsx display)
  const labelText = String(observations?.label_details ?? "");
  const originMatch = labelText.match(/(?:made in|country of origin|origin)[\s:]+([A-Z][A-Za-z .'-]{1,30})/i);
  const originRaw = originMatch ? originMatch[1].trim() : "";
  // Strip trailing punctuation and title-case (e.g. "KOREA'" → "Korea", "USA" stays "USA")
  const originCleaned = originRaw.replace(/[^A-Za-z\s]+$/, "").replace(/^[^A-Za-z]+/, "").trim();
  const origin = originCleaned.length <= 3
    ? originCleaned.toUpperCase()
    : originCleaned.toLowerCase().replace(/(^|\s)([a-z])/g, (_, sp, ch) => sp + ch.toUpperCase());

  const detectedType = String(item.detected_type || meta.item_category || "Vintage item");
  const resolvedBrand =
    String(item.brand_detected ?? "").trim() ||
    String(userBrandFallback ?? "").trim();
  const titleCasedBrand = resolvedBrand
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
  const title = titleCasedBrand
    ? (detectedType ? `${titleCasedBrand} ${detectedType}` : titleCasedBrand)
    : detectedType;

  return {
    title,
    brand: String(item.brand_detected ?? "") || resolvedBrand || "",
    era: String(item.estimated_era ?? ""),
    origin,
    category: String(meta.item_category || item.category || ""),
    material: String(item.material_description ?? ""),
    size: String(item.size ?? ""),
    color: String(item.color_description ?? ""),
    condition: String(pricing?.condition_tier ?? ""),
    condition_notes: String(guidance?.condition_note ?? ""),
    verdict: isPass ? "PASS" : "FAIL",
    verdict_label: verdictLabelMap[verdictKey] ?? "Inconclusive",
    confidence,
    rarity: String(assessment?.rarity ?? ""),
    tags,
    notes,
    forensic,
    price_low: recLow,
    price_suggested: suggested,
    price_high: recHigh,
    analyzed_count: Number(meta.photos_submitted ?? 0) || undefined,
    verification: null,
    pricing_comparable_count: comparableCount || undefined,
    pricing_data_source: dataSource !== "none" ? dataSource : undefined,
  };
}